// Copy-generator för lokala områdessidor: /filmproduktion/<ort> och
// /filmbyra/<ort>. Samma struktur som cityHubCopy men för de två primära
// (icke-canonicaliserade) hubbarna, och med stöd för stadsdelar (partOf).

import type { ReactNode } from "react";
import type { LandingProps, LandingFAQ } from "@/components/site/landing/LandingPage";
import type { CityMeta } from "./cities";
import type { CityHubPage } from "./cityHubCopy";
import { buildRelatedHubs, type HubKind } from "./related-hubs";
import { presenceFor, locationSentence } from "./presence";

function L({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
      href={href}
    >
      {children}
    </a>
  );
}

function joinList(items: string[], max = 3): string {
  const slice = items.slice(0, max);
  if (slice.length <= 1) return slice.join("");
  return slice.slice(0, -1).join(", ") + " och " + slice.at(-1);
}

/** "Stockholm" för stadsdelar, annars regionen. */
function areaLabel(city: CityMeta): string {
  return city.partOf ? `${city.name} och ${city.partOf}` : city.regionLong;
}

/**
 * Ärlig närvarorad. Vi påstår aldrig lokal etablering där vi inte har
 * kontor — vi beskriver i stället hur produktionen görs på plats.
 */
function placeLine(city: CityMeta): string {
  return presenceFor(city.slug, city.name).line;
}

/** "Vi filmar i Ideon Science Park, Medicon Village och …" */
function locationsLine(city: CityMeta): string {
  const presence = presenceFor(city.slug, city.name);
  const sites = locationSentence(presence);
  const travel = presence.travelNote ? ` ${presence.travelNote}.` : "";
  return `Vanliga inspelningsmiljöer i ${city.name}: ${sites}.${travel}`;
}

/* -------------------------------------------------------------------------- */
/* FILMPRODUKTION                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Orter där Search Console visar tydlig drönarintention ("drönarfilm <ort>").
 * Sidorna nämner drönarfilm i titel och beskrivning så snippet matchar sökningen.
 */
const DRONE_DEMAND = new Set([
  "nykoping",
  "sodertalje",
  "taby",
  "nacka",
  "solna",
  "uppsala",
  "enkoping",
]);

/**
 * Orter där Search Console visar att "reklamfilm <ort>" är den dominerande
 * sökningen — titeln matchar då den frasen i stället för den generiska.
 */
const AD_DEMAND = new Set(["borlange", "falun", "karlstad"]);

export function filmproduktionForCity(city: CityMeta): CityHubPage {
  const drone = DRONE_DEMAND.has(city.slug);
  const ad = AD_DEMAND.has(city.slug);
  const industries = joinList(city.industries);
  const neighbors = joinList(city.neighbors);
  const path = `/filmproduktion/${city.slug}`;

  const faq: LandingFAQ[] = [
    {
      q: `Var i ${city.name} filmar ni?`,
      a: `${locationsLine(city)} Vi filmar också på er egen arbetsplats eller i hyrd studio i ${areaLabel(city)}.`,
    },
    {
      q: `Vad kostar filmproduktion i ${city.name}?`,
      a: "Samma prislogik som i resten av Sverige, utan ortspåslag. Social-content: 25–60 kkr. Kundcase och företagsfilm: 60–160 kkr. Reklamfilm: 150–450 kkr. Pulse-abonnemang från 14 995 kr/mån för löpande filmflöde.",
    },
    {
      q: `Har ni kontor i ${city.name}?`,
      a: placeLine(city),
    },
    {
      q: `Hur snabbt kan ni vara på plats i ${city.name}?`,
      a: `Första möte inom 5 arbetsdagar. Inspelning bokas normalt inom 1–3 veckor beroende på säsong. Pulse-kunder får första klippet inom 10–14 dagar.`,
    },
    {
      q: `Vilka branscher i ${city.name} har ni erfarenhet av?`,
      a: `Vi har särskild vana av ${industries}. ${city.edge}`,
    },
    {
      q: `Reser ni vidare till ${neighbors}?`,
      a: `Ja. Vi planerar produktionsdagen så flera inspelningsplatser i ${areaLabel(city)} kan klaras av samma dag — ni betalar inte för dödtid.`,
    },
  ];

  const props: LandingProps = {
    eyebrow: `Filmproduktion · ${city.name}`,
    title: <>Filmproduktion i {city.name} — utan månader av väntan.</>,
    lede: `Fenny producerar reklamfilm, företagsfilm, kundcase och employer branding i ${city.name}. Strategi, manus, regi, foto, ljud, klipp och versionering i samma team — Pulse-abonnemang för löpande flöde eller Prime-projekt till fast pris.`,
    tldr: (
      <p>
        Fenny gör <strong>filmproduktion i {city.name}</strong> — hela kedjan
        inhouse. <strong>Pulse från 14 995 kr/mån</strong> för löpande
        filmflöde, eller Prime-projekt till fastpris (40 000–700 000 kr).{" "}
        {city.edge}
      </p>
    ),
    ctaPrimary: { label: `Boka möte i ${city.name}`, to: "/kontakt" },
    ctaSecondary: { label: "Se case", to: "/case" },
    trackingId: `filmproduktion-${city.slug}`,
    bullets: [
      {
        title: presenceFor(city.slug, city.name).label,
        body: placeLine(city),
      },
      {
        title: "Snabb start",
        body: "Första möte inom en vecka. Första klipp inom 10–14 dagar för Pulse-kunder.",
      },
      {
        title: "Hela kedjan",
        body: "Strategi, manus, regi, foto, ljud, klipp, grade och versionering. Ni jobbar mot ett team.",
      },
    ],
    sections: [
      {
        id: "vem",
        heading: `Vem jobbar vi med i ${city.name}?`,
        body: (
          <p>
            Bolag inom {industries} i {areaLabel(city)} — från etablerade
            varumärken med löpande contentbehov till tillväxtbolag som ska
            lansera. {city.edge}
          </p>
        ),
      },
      {
        id: "pa-plats",
        heading: `Så ser en inspelningsdag i ${city.name} ut`,
        body: (
          <p>
            {locationsLine(city)} Vi scoutar i förväg, stämmer av access och
            tider med er platsansvarige och söker de drönartillstånd som
            gäller för aktuella zoner innan vi flyger. Ljud, ljus och kamera
            packas efter miljön — produktionsgolv, kontor eller utomhus.
          </p>
        ),
      },
      {
        id: "tjanster",
        heading: `Vad producerar vi i ${city.name}?`,
        body: (
          <p>
            <L href="/reklamfilm">Reklamfilm</L> för paid social och YouTube,{" "}
            <L href="/foretagsfilm">företagsfilm</L> och{" "}
            <L href="/kundcase-film">kundcase</L>,{" "}
            <L href="/employer-branding-film">employer branding</L>,{" "}
            <L href="/produktfilm">produktfilm</L>,{" "}
            <L href="/dronarfilm">drönarfilm</L> och{" "}
            <L href="/sociala-medier-film">film för sociala medier</L>.
          </p>
        ),
      },
      ...(drone
        ? [
            {
              id: "dronarfilm",
              heading: `Drönarfilm i ${city.name}`,
              body: (
                <p>
                  Vi flyger med certifierad pilot, full försäkring och de
                  tillstånd som krävs i {areaLabel(city)} — anläggningar,
                  fastigheter, event och landskap. Luftbilderna klipps ihop med
                  markmaterialet i samma produktion.{" "}
                  <L href="/dronarfilm">Läs mer om drönarfilm</L>.
                </p>
              ),
            },
          ]
        : []),
      {
        id: "andra-vinklar",
        heading: `Andra vinklar för ${city.name}`,
        body: (
          <p>
            Söker ni en byrå snarare än en produktion? Se{" "}
            <L href={`/filmbyra/${city.slug}`}>filmbyrå i {city.name}</L> eller{" "}
            <L href={`/employer-branding-film/${city.slug}`}>
              employer branding-film i {city.name}
            </L>
            . Priser hittar ni på <L href="/priser">prissidan</L>.
          </p>
        ),
      },
    ],
    faq,
    caseLimit: 3,
    closing: {
      title: `Möts vi i ${city.name}?`,
      body: "30 minuter på ert kontor eller över video — vi går igenom era mål och vad vi skulle göra först.",
      primary: { label: "Boka strategimöte", to: "/kontakt" },
      secondary: { label: "Räkna pris", to: "/priser" },
    },
    related: buildRelatedHubs(city, "filmproduktion" as HubKind),
  };

  return {
    props,
    faq,
    meta: {
      title: drone
        ? `Filmproduktion ${city.name} — film och drönarfilm på plats`
        : ad
          ? `Filmproduktion i ${city.name} — reklamfilm med team på plats`
          : `Filmproduktion ${city.name} — offert med fast pris inom 48 h`,
      description: drone
        ? `Filmteam i ${city.name} för reklamfilm, företagsfilm, kundcase och drönarfilm med certifierad pilot och tillstånd. Möte inom en vecka, första klippet inom 10–14 dagar.`
        : `Filmteam i ${city.name} för reklamfilm, företagsfilm och kundcase åt ${industries}. Möte inom en vecka, offert med fast pris inom 48 h, första klippet inom 10–14 dagar.`,
      ogTitle: `Filmproduktion ${city.name} — Fenny`,
      ogDescription: `Filmbyrå i ${city.name} med hela kedjan inhouse — strategi, regi, foto, klipp.`,
      path,
      schemaName: `Fenny — filmproduktion i ${city.name}. Reklamfilm, företagsfilm, kundcase och employer branding.`,
      breadcrumbLabel: city.name,
      breadcrumbHubLabel: "Filmproduktion",
      breadcrumbHubPath: "/filmproduktion",
    },
  };
}

/* -------------------------------------------------------------------------- */
/* FILMBYRÅ                                                                   */
/* -------------------------------------------------------------------------- */

export function filmbyraForCity(city: CityMeta): CityHubPage {
  const industries = joinList(city.industries);
  const neighbors = joinList(city.neighbors);
  const path = `/filmbyra/${city.slug}`;

  const faq: LandingFAQ[] = [
    {
      q: `Levererar Fenny i ${city.name}?`,
      a: `Ja. Vi producerar för bolag i ${areaLabel(city)} och närliggande orter som ${neighbors} — med särskild vana av ${industries}. ${placeLine(city)}`,
    },
    {
      q: `Vad kostar en filmbyrå i ${city.name}?`,
      a: "Vi prissätter på leveransvolym, inte på ort. Pulse-abonnemang från 14 995 kr/mån för löpande filmflöde. Prime-projekt 50–900 kkr. Inga reseavgifter inom upptagningsområdet.",
    },
    {
      q: `Vad skiljer en filmbyrå från ett produktionsbolag?`,
      a: "Ett produktionsbolag utför en beställd film. En filmbyrå tar även ansvar för strategi, koncept, format och distribution — vi mäter på affärsresultat, inte på leveransfil.",
    },
    {
      q: `Hur snabbt får vi första filmen?`,
      a: `Pulse-kunder får första klippet inom 10–14 dagar från strategimötet. Prime-produktioner levereras på 3–6 veckor. För akuta behov i ${city.name} kan vi ofta planera inspelning inom en vecka.`,
    },
  ];

  const props: LandingProps = {
    eyebrow: `Filmbyrå · ${city.name}`,
    title: <>Filmbyrå i {city.name} — strategi, produktion och flöde.</>,
    lede: `Fenny är en filmbyrå för bolag i ${city.name} och ${areaLabel(city)}. Vi tar ansvar för hela kedjan: vad filmen ska göra, hur den produceras och hur den versioneras för era kanaler.`,
    tldr: (
      <p>
        <strong>Filmbyrå i {city.name}</strong> med strategi, regi, foto, klipp
        och versionering inhouse. <strong>Pulse från 14 995 kr/mån</strong>{" "}
        eller Prime-projekt till fastpris. {city.edge}
      </p>
    ),
    ctaPrimary: { label: "Boka strategimöte", to: "/kontakt" },
    ctaSecondary: { label: "Se case", to: "/case" },
    trackingId: `filmbyra-${city.slug}`,
    bullets: [
      {
        title: "Strategi först",
        body: "Vi börjar i affärsmålet och kanalen — sedan bestäms format, längd och antal filmer.",
      },
      {
        title: "Volym i takt",
        body: "Pulse levererar 4–20 färdiga filmer per månad, versionerade per kanal.",
      },
      {
        title: presenceFor(city.slug, city.name).label,
        body: placeLine(city),
      },
    ],
    sections: [
      {
        id: "arbetssatt",
        heading: `Så jobbar vi med kunder i ${city.name}`,
        body: (
          <p>
            Strategimöte, filmplan för kvartalet, inspelningsdagar i block och
            löpande leverans. Ni godkänner skriftligt efter varje fas.{" "}
            <L href="/process">Se hela processen</L>.
          </p>
        ),
      },
      {
        id: "pa-plats",
        heading: `Var vi filmar i ${city.name}`,
        body: <p>{locationsLine(city)}</p>,
      },
      {
        id: "branscher",
        heading: `Branscher vi kan i ${areaLabel(city)}`,
        body: (
          <p>
            {industries.charAt(0).toUpperCase() + industries.slice(1)}.{" "}
            {city.edge} Vi har också vana av produktioner i{" "}
            {neighbors}.
          </p>
        ),
      },
      {
        id: "andra-vinklar",
        heading: `Andra vinklar för ${city.name}`,
        body: (
          <p>
            Vill ni bara ha produktionen utförd? Se{" "}
            <L href={`/filmproduktion/${city.slug}`}>
              filmproduktion i {city.name}
            </L>{" "}
            eller jämför{" "}
            <L href="/jamfor/filmbyra-vs-frilansare">byrå mot frilansare</L>.
          </p>
        ),
      },
    ],
    faq,
    caseLimit: 3,
    closing: {
      title: `Ska vi ses i ${city.name}?`,
      body: "30 minuter där vi mappar era mål mot tre konkreta nästa steg.",
      primary: { label: "Boka strategimöte", to: "/kontakt" },
      secondary: { label: "Räkna pris", to: "/priser" },
    },
    related: buildRelatedHubs(city, "filmbyra" as HubKind),
  };

  return {
    props,
    faq,
    meta: {
      title: `Filmbyrå i ${city.name} — film varje månad, inte en om året`,
      description: `Filmbyrå i ${city.name} som sköter strategi, inspelning och klipp åt ${industries} — löpande filmflöde eller enskilda kampanjer, med fast pris innan start.`,
      ogTitle: `Filmbyrå ${city.name} — Fenny`,
      ogDescription: `Strategi, produktion och versionering i samma team — filmbyrå i ${city.name}.`,
      path,
      schemaName: `Fenny — filmbyrå i ${city.name}. Strategi, filmproduktion och löpande contentleverans.`,
      breadcrumbLabel: city.name,
      breadcrumbHubLabel: "Filmbyrå",
      breadcrumbHubPath: "/filmbyra",
    },
  };
}
